import { API_CONFIG } from './config';
import { httpService } from './http';
import { toApiVisibility, toBandPostUiVisibility, toUiVisibility } from '../../utils/visibility';

const isBandRelatedPost = (post) => (
    post?.relatedObject
    && String(post.relatedObject.objectType || '').trim().toUpperCase() === 'BAND'
);

class PostsService {
    normalizePost = (post) => ({
        ...post,
        attachments: Array.isArray(post.attachments) ? post.attachments : [],
        reactions: Array.isArray(post.reactions) ? post.reactions : [],
        recentComments: Array.isArray(post.recentComments) ? post.recentComments : [],
        commentsCount: typeof post.commentsCount === 'number' ? post.commentsCount : 0,
        relatedObject: post?.relatedObject && typeof post.relatedObject === 'object'
            ? post.relatedObject
            : null,
        visibilityLabel: isBandRelatedPost(post)
            ? toBandPostUiVisibility(post.visibility)
            : toUiVisibility(post.visibility || post.visibilityLabel),
    });

    normalizePosts = (posts) => (Array.isArray(posts) ? posts.map(this.normalizePost) : []);

    normalizePostPage = (response, options = {}) => {
        if (Array.isArray(response)) {
            return {
                items: this.normalizePosts(response),
                hasMore: false,
                nextOffset: null,
                limit: null,
                offset: typeof options.offset === 'number' ? Math.max(0, Math.floor(options.offset)) : 0,
            };
        }

        const items = this.normalizePosts(response?.items);
        const offset = typeof response?.offset === 'number'
            ? response.offset
            : (typeof options.offset === 'number' ? Math.max(0, Math.floor(options.offset)) : 0);
        const limit = typeof response?.limit === 'number'
            ? response.limit
            : (typeof options.limit === 'number' ? Math.max(1, Math.floor(options.limit)) : null);
        const nextOffset = typeof response?.nextOffset === 'number'
            ? response.nextOffset
            : null;

        return {
            items,
            hasMore: response?.hasMore === true,
            nextOffset,
            limit,
            offset,
        };
    };

    withOptionalPagination(endpoint, options = {}) {
        const params = new URLSearchParams();
        if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
            params.set('limit', String(Math.max(1, Math.floor(options.limit))));
        }
        if (typeof options.offset === 'number' && Number.isFinite(options.offset)) {
            params.set('offset', String(Math.max(0, Math.floor(options.offset))));
        }
        if (options.audience === 'public') {
            params.set('audience', 'public');
        }
        const query = params.toString();
        return query ? `${endpoint}?${query}` : endpoint;
    }

    async fetchUserPosts() {
        const posts = await httpService.get(API_CONFIG.ENDPOINTS.POSTS.FETCH_USER_POSTS);
        return this.normalizePosts(posts);
    }

    async fetchUserPostsPage(options = {}) {
        const response = await httpService.get(
            this.withOptionalPagination(API_CONFIG.ENDPOINTS.POSTS.FETCH_USER_POSTS, options),
            options,
        );
        return this.normalizePostPage(response, options);
    }

    async fetchUserPostsById(userId) {
        const posts = await httpService.get(API_CONFIG.ENDPOINTS.POSTS.FETCH_USER_BY_ID(userId));
        return this.normalizePosts(posts);
    }

    async fetchUserPostsByIdPage(userId, options = {}) {
        const response = await httpService.get(
            this.withOptionalPagination(API_CONFIG.ENDPOINTS.POSTS.FETCH_USER_BY_ID(userId), options),
            options,
        );
        return this.normalizePostPage(response, options);
    }

    async fetchPublicUserPostsByIdPage(userId, options = {}) {
        return this.fetchUserPostsByIdPage(userId, {
            ...options,
            audience: 'public',
        });
    }

    async fetchBandPosts(bandObjectId) {
        const posts = await httpService.get(API_CONFIG.ENDPOINTS.POSTS.FETCH_BAND_POSTS(bandObjectId));
        return this.normalizePosts(posts);
    }

    async fetchBandPostsPage(bandObjectId, options = {}) {
        const response = await httpService.get(
            this.withOptionalPagination(API_CONFIG.ENDPOINTS.POSTS.FETCH_BAND_POSTS(bandObjectId), options),
            options,
        );
        return this.normalizePostPage(response, options);
    }

    async fetchPublicBandPostsPage(bandObjectId, options = {}) {
        return this.fetchBandPostsPage(bandObjectId, {
            ...options,
            audience: 'public',
        });
    }

    async fetchFeed() {
        const posts = await httpService.get(API_CONFIG.ENDPOINTS.POSTS.FEED);
        return this.normalizePosts(posts);
    }

    async fetchFeedPage(options = {}) {
        const response = await httpService.get(
            this.withOptionalPagination(API_CONFIG.ENDPOINTS.POSTS.FEED, options),
            options,
        );
        return this.normalizePostPage(response, options);
    }

    async createPost(postData) {
        const { id, objectId, ...safePostData } = postData || {};
        const response = await httpService.post(
            API_CONFIG.ENDPOINTS.POSTS.CREATE,
            {
                ...safePostData,
                visibility: toApiVisibility(safePostData.visibility),
                attachments: Array.isArray(safePostData.attachments) ? safePostData.attachments : [],
            }
        );
        return this.normalizePost(response);
    }

    async createBandPost(bandObjectId, postData) {
        const { id, objectId, relatedObject, relatedObjectType, relatedObjectId, ...safePostData } = postData || {};
        const response = await httpService.post(
            API_CONFIG.ENDPOINTS.POSTS.CREATE_BAND_POST(bandObjectId),
            {
                ...safePostData,
                visibility: toApiVisibility(safePostData.visibility),
                attachments: Array.isArray(safePostData.attachments) ? safePostData.attachments : [],
            }
        );
        return this.normalizePost(response);
    }

    async updatePost(postId, postData) {
        const response = await httpService.put(
            API_CONFIG.ENDPOINTS.POSTS.UPDATE(postId),
            {
                ...postData,
                visibility: toApiVisibility(postData.visibility),
                attachments: Array.isArray(postData.attachments) ? postData.attachments : [],
            }
        );
        return this.normalizePost(response);
    }

    async deletePost(postId) {
        await httpService.delete(API_CONFIG.ENDPOINTS.POSTS.DELETE(postId));
    }

    async addReaction(postId, emoji) {
        const reactions = await httpService.post(
            API_CONFIG.ENDPOINTS.POSTS.REACTIONS(postId),
            { emoji }
        );
        return Array.isArray(reactions) ? reactions : [];
    }

    async removeReaction(postId, emoji) {
        const reactions = await httpService.delete(
            `${API_CONFIG.ENDPOINTS.POSTS.REACTIONS(postId)}?emoji=${encodeURIComponent(emoji)}`
        );
        return Array.isArray(reactions) ? reactions : [];
    }

    async fetchReactions(postId) {
        const reactions = await httpService.get(API_CONFIG.ENDPOINTS.POSTS.REACTIONS(postId));
        return Array.isArray(reactions) ? reactions : [];
    }

    async addComment(postId, content, parentId = null) {
        const comment = await httpService.post(
            API_CONFIG.ENDPOINTS.POSTS.COMMENTS(postId),
            { content, parentId }
        );
        return comment;
    }

    async updateComment(postId, commentId, content) {
        const comment = await httpService.patch(
            `${API_CONFIG.ENDPOINTS.POSTS.COMMENTS(postId)}/${encodeURIComponent(commentId)}`,
            { content }
        );
        return comment;
    }

    async deleteComment(postId, commentId) {
        await httpService.delete(
            `${API_CONFIG.ENDPOINTS.POSTS.COMMENTS(postId)}/${encodeURIComponent(commentId)}`
        );
    }

    async fetchComments(postId, page = 0, size = 20) {
        const response = await httpService.get(
            `${API_CONFIG.ENDPOINTS.POSTS.COMMENTS(postId)}?page=${page}&size=${size}`
        );
        return {
            comments: Array.isArray(response?.comments) ? response.comments : [],
            total: typeof response?.total === 'number' ? response.total : 0,
        };
    }
}

export const postsService = new PostsService();
